"use client";

import { useEffect, useRef, useState } from 'react';
import { Play, Music, Video as VideoIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AudioPlayer } from '@/components/AudioPlayer';
import { VideoPlayer } from '@/components/VideoPlayer';

interface SeamlessMediaDisplayProps {
  file_url: string;
  file_type: 'image' | 'video' | 'audio';
  thumbnail_url?: string;
  title?: string;
  className?: string;
}

export default function SeamlessMediaDisplay({
  file_url,
  file_type,
  thumbnail_url,
  title,
  className = ''
}: SeamlessMediaDisplayProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [hasError, setHasError] = useState(false);

  // IntersectionObserver for auto-play/pause when scrolling
  useEffect(() => {
    if (file_type !== 'video' || !videoRef.current || !containerRef.current) {
      return;
    }

    const video = videoRef.current;
    const container = containerRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Video is in viewport - play it
            video.play().catch((error) => {
              // Autoplay may fail due to browser policies
              console.log('Autoplay prevented:', error);
              setShowPlayButton(true);
            });
            setIsPlaying(true);
          } else {
            // Video is out of viewport - pause it
            video.pause();
            setIsPlaying(false);
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of video is visible
        rootMargin: '0px'
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [file_type, file_url]);

  // Handle video play/pause
  const handleVideoPlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
      setShowPlayButton(false);
    }
  };

  const handleVideoPause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
      setShowPlayButton(true);
    }
  };

  // Handle video errors
  const handleVideoError = () => {
    setHasError(true);
    setShowPlayButton(true);
  };

  // Handle video metadata load (for poster/thumbnail)
  const handleVideoLoadedMetadata = () => {
    // Video metadata loaded successfully
    if (videoRef.current && !thumbnail_url) {
      // If no explicit thumbnail, the browser will use the first frame
      // We can optionally seek to a specific time to get a better frame
      videoRef.current.currentTime = 0.1; // Seek to 0.1s for a better thumbnail
    }
  };

  if (file_type === 'image') {
    return (
      <div className={`rounded-lg overflow-hidden ${className}`}>
        <img
          src={file_url}
          alt={title || 'Post image'}
          className="w-full h-auto object-cover"
          onError={() => setHasError(true)}
        />
        {hasError && (
          <div className="w-full h-64 bg-slate-700 flex items-center justify-center">
            <span className="text-slate-400">Failed to load image</span>
          </div>
        )}
      </div>
    );
  }

  if (file_type === 'video') {
    return (
      <div className={className}>
        <VideoPlayer
          src={file_url}
          poster={thumbnail_url}
          className="w-full"
        />
      </div>
    );
  }

  if (file_type === 'audio') {
    return (
      <div className={className}>
        <AudioPlayer
          src={file_url}
          title={title}
          className="w-full"
        />
      </div>
    );
  }

  return null;
}

